import React from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';

export type ProjectPageMode = 'view' | 'edit';

type ProjectModeToggleProps = {
  mode: ProjectPageMode;
  onChange: (mode: ProjectPageMode) => void;
  canEdit?: boolean;
  className?: string;
};

/**
 * Compact View / Edit control for a project. View is read-only workspace;
 * Edit unlocks the same page (not the settings form at /edit).
 */
export const ProjectModeToggle: React.FC<ProjectModeToggleProps> = ({
  mode,
  onChange,
  canEdit = true,
  className = ''
}) => {
  const { t } = useLanguage();

  const segmentClass = (selected: boolean, disabled?: boolean) =>
    `px-2.5 py-1 text-xs font-medium rounded ${
      selected
        ? 'bg-[var(--color-accent-bg)] text-[var(--color-accent-text)]'
        : 'text-[var(--color-secondary-text)] hover:text-[var(--color-surface-text)]'
    } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`;

  return (
    <div
      className={`inline-flex rounded-md border border-[var(--color-border)] bg-[var(--color-surface-bg)] p-0.5 ${className}`}
      role="group"
      aria-label={t('transformation.mode.label')}
    >
      <button
        type="button"
        className={segmentClass(mode === 'view')}
        aria-pressed={mode === 'view'}
        onClick={() => onChange('view')}
      >
        {t('transformation.mode.view')}
      </button>
      <button
        type="button"
        className={segmentClass(mode === 'edit', !canEdit)}
        aria-pressed={mode === 'edit'}
        disabled={!canEdit}
        onClick={() => canEdit && onChange('edit')}
      >
        {t('transformation.mode.edit')}
      </button>
    </div>
  );
};
